// Generated by TaroBase SDK Generator: 2026-04-14T11:42:05.667Z
// Collection: media/$mediaId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Admin-managed media gallery for Danger Dan! landing page
 */

export interface MediaRequest {
  type: string;
  url: string;
  title?: string;
  order: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface MediaResponse {
  type: string;
  url: string;
  title?: string;
  order: number;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a Media operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildMedia(mediaId: string, data: MediaRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `media/${mediaId}`, document: doc };
}

/**
 * Admin only. Adds a new media item (image or video) with a type, URL, display order, and optional title. The type field is immutable after creation. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setMedia(mediaId: string, data: MediaRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`media/${mediaId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting Media: ${formatError(error)}`);
    return false;
  }
}

export type MediaRequestUpdate = Partial<MediaRequest>;

/**
 * Build a Media update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateMedia(mediaId: string, data: MediaRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `media/${mediaId}`, document: doc };
}

/**
 * Admin only. Can update url (to change the file), title (to change the caption), and order (to reorder). The type field cannot be changed. (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateMedia(mediaId: string, data: MediaRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`media/${mediaId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating Media: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read media entries. Used to display gallery images and video teaser on the public pre-launch landing page.
   (Get Single Item)
 */
export async function getMedia(mediaId: string): Promise<MediaResponse | null> {
  try {
    const result = await get(`media/${mediaId}`);
    return result as MediaResponse | null;
  } catch (error) {
    console.error(`Error getting Media: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single Media document. (
  Read Operation Details: Anyone can read media entries. Used to display gallery images and video teaser on the public pre-launch landing page.
  )
 */
export function subscribeMedia(callback: (data: MediaResponse | null) => void, mediaId: string): Promise<() => Promise<void>> {
  return subscribe(`media/${mediaId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as MediaResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to Media at path '${`media/${mediaId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many Media items from collection media
 
  Read Operation Details: Anyone can read media entries. Used to display gallery images and video teaser on the public pre-launch landing page.
  
 */
export async function getManyMedia(filter: string = ""): Promise<MediaResponse[]> {
  try {
    const results = await get("media", { prompt: filter });
    return Array.isArray(results) ? results as MediaResponse[] : [];
  } catch (error) {
    console.error(`Error getting Media collection at path 'media': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Media collection at media
 
  Read Operation Details: Anyone can read media entries. Used to display gallery images and video teaser on the public pre-launch landing page.
  
 */
export function subscribeManyMedia(callback: (data: MediaResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("media", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as MediaResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Media collection at path '${"media"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all Media items from collection media
 
  Read Operation Details: Anyone can read media entries. Used to display gallery images and video teaser on the public pre-launch landing page.
  
 */
export async function getAllMedia(filter: string = ""): Promise<MediaResponse[]> {
  try {
    const results = await get("media", { prompt: filter });
    return Array.isArray(results) ? results as MediaResponse[] : [];
  } catch (error) {
    console.error(`Error getting Media collection at path 'media': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in Media collection at media
 
  Read Operation Details: Anyone can read media entries. Used to display gallery images and video teaser on the public pre-launch landing page.
  
 */
export function subscribeAllMedia(callback: (data: MediaResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("media", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as MediaResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to Media collection at path '${"media"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count Media items in collection media.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countMedia(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("media", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting Media at path 'media': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on Media items in collection media.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateMedia(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("media", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating Media at path 'media': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: Admin only. Removes a media entry from the gallery.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteMedia(mediaId: string): Promise<boolean> {
  try {
    const succeeded = await set(`media/${mediaId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting Media: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for Media for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteMedia(mediaId: string): DocumentOperation {
  return { path: `media/${mediaId}`, document: null };
}

